import { EventName } from "module/analytics/types"
import { useLiquidity } from "module/pool/hook/useLiquidity"
import { Web3WalletContainer } from "module/wallet/container/Web3WalletContainer"
import { formatNumberUSD } from "util/format"

import { Alert, AlertDescription, AlertIcon, AlertTitle, Text } from "@chakra-ui/react"
import { MarketStatus } from "@perp/sdk-curie"
import { PerpLink } from "component/PerpLink"
import { TxButton } from "component/TxButton"
import { Link } from "react-router-dom"
import { useAsync } from "react-use"
import { usePositionTaker } from "sdk-react/clearingHouse/usePositionTaker"
import { useMarket } from "sdk-react/markets/useMarket"
import { usePerpTransactionSender } from "sdk-react/perpetualProtocol/usePerpTransactionSender"

interface EmergencyShutdownWarningProps {
    tickerSymbol: string
}

const DELIST_CONFIG: Record<string, { discordChannelUrl: string }> = {
    ONEUSD: {
        discordChannelUrl: "https://discord.com/channels/687397941383659579/1054512079375192125",
    },
    LUNAUSD: {
        discordChannelUrl: "https://discord.gg/rRPtrpKJ",
    },
    DEFAULT: {
        // NOTE: announcement channel
        discordChannelUrl: "https://discord.com/channels/687397941383659579/739471229706043478",
    },
}

export function EmergencyShutdownWarning({ tickerSymbol }: EmergencyShutdownWarningProps) {
    const { status: marketStatus, market } = useMarket(tickerSymbol)

    const { account } = Web3WalletContainer.useContainer()
    const { totalLiquidity } = useLiquidity(tickerSymbol)
    const hasNoLiquidity = !totalLiquidity || totalLiquidity.lte(0)
    const position = usePositionTaker(tickerSymbol)
    const hasPosition = !!position

    const canQuitMarket = !!account && hasNoLiquidity && hasPosition

    const quitMarket = usePerpTransactionSender(
        ({ clearingHouse }) => {
            return clearingHouse.quitMarket(tickerSymbol)
        },
        [tickerSymbol],
    )

    const delistConfig = DELIST_CONFIG[tickerSymbol] || DELIST_CONFIG["DEFAULT"]
    const discordChannelUrl = delistConfig.discordChannelUrl

    const settlementPriceState = useAsync(async () => {
        const marketStatus = await market?.getStatus()
        if (marketStatus !== MarketStatus.CLOSED) {
            return
        }
        return await market?.getClosedPrice()
    }, [market])
    const settlementPriceStr = settlementPriceState.value
        ? formatNumberUSD({ num: settlementPriceState.value })
        : "-,--"

    switch (marketStatus) {
        case MarketStatus.PAUSED: {
            return (
                <Alert
                    status="warning"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    py={5}
                    mt={6}
                    mb={2}
                >
                    <AlertIcon boxSize="32px" mr={0} />
                    <AlertTitle mt={4} mb={1} fontSize="lg">
                        Market Paused
                    </AlertTitle>
                    <AlertDescription maxWidth="lg">
                        This market is paused and your position is frozen at the moment.
                        <br />
                        You will be able to close it once the final price is determined.
                        <br />
                        Please visit our{" "}
                        <PerpLink href={discordChannelUrl} trackingProps={{ eventName: EventName.LINK_DISCORD }}>
                            <Text as="span" color="perpetual.500">
                                Discord channel
                            </Text>
                        </PerpLink>{" "}
                        for the latest update. Other markets are unaffected.
                    </AlertDescription>
                </Alert>
            )
        }
        case MarketStatus.CLOSED: {
            return (
                <Alert
                    status="error"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    py={5}
                    mt={6}
                    mb={2}
                >
                    <AlertIcon boxSize="32px" mr={0} />
                    <AlertTitle mt={4} mb={1} fontSize="lg">
                        Market Closed
                    </AlertTitle>

                    {/*
                        check if there are remaining positions
                    */}

                    {canQuitMarket ? (
                        <AlertDescription maxWidth="lg">
                            This market has been closed. All remaining positions will be settled according to the
                            settlementPrice ({`${settlementPriceStr}`})
                            <br /> <br />
                            Please close your position as soon as possible.
                            <br />
                            <br />
                            <TxButton disabled={!canQuitMarket} onClick={quitMarket}>
                                Close Position
                            </TxButton>
                            <br />
                            <br />
                            If you have liquidity in this market, please remove your liquidity first and close your
                            position. Please visit our{" "}
                            <PerpLink
                                href={discordChannelUrl}
                                color="perpetual.500"
                                trackingProps={{ eventName: EventName.LINK_DISCORD }}
                            >
                                <Text as="span" color="perpetual.500">
                                    Discord channel
                                </Text>
                            </PerpLink>{" "}
                            if you need any support. <br /> <br />
                        </AlertDescription>
                    ) : (
                        <AlertDescription maxWidth="lg">
                            This market has been closed. All remaining positions were settled according to the
                            settlementPrice({`${settlementPriceStr}`}).{" "}
                            <Link to="/history/trade">
                                <Text as="span" color="perpetual.500">
                                    Check the trade history here.
                                </Text>
                            </Link>
                        </AlertDescription>
                    )}
                </Alert>
            )
        }
        default:
            return null
    }
}
